if (!customElements.get('header-section')) {
  customElements.define(
    'header-section',
    class HeaderSection extends HTMLElement {
      // DOM Elements
      private menuToggle: HTMLButtonElement | null = null;
      private menuWrapper: HTMLElement | null = null;
      private mainOverlay: HTMLElement | null = null;
      private menuItems: NodeListOf<HTMLElement> | null = null;
      private headerElement: HTMLElement | null = null;
      private announcementBar: HTMLElement | null = null;

      // State
      private lastScrollY = 0;
      private isScrollUpdatePending = false;

      connectedCallback(): void {
        this.cacheElements();
        this.setupEventListeners();
        this.initializeHeader();
        this.resetMobileMenuAnimations();
      }

      disconnectedCallback(): void {
        this.cleanupEventListeners();
        document.body.classList.remove('overflow-hidden');
      }

      private cacheElements(): void {
        this.menuToggle = this.querySelector('[data-menu-toggle]');
        this.menuWrapper = this.querySelector('[data-menu-wrapper-mobile]');
        this.mainOverlay = document.querySelector('[data-main-overlay]');
        this.menuItems = this.querySelectorAll('[data-navigation-main-link]');
        this.headerElement = this.closest('header');
        this.announcementBar = document.querySelector('.announcement-bar');
      }

      private setupEventListeners(): void {
        // Menu toggle events
        this.menuToggle?.addEventListener(
          'click',
          this.handleMenuToggle.bind(this)
        );

        // Window events
        window.addEventListener('resize', this.handleResize.bind(this), {
          passive: true,
        });
        window.addEventListener('scroll', this.handleScroll.bind(this), {
          passive: true,
        });

        // Menu item hover events
        this.menuItems?.forEach((item) => {
          item.addEventListener(
            'mouseenter',
            this.handleMenuItemEnter.bind(this)
          );
          item.addEventListener(
            'mouseleave',
            this.handleMenuItemLeave.bind(this)
          );
        });
      }

      private cleanupEventListeners(): void {
        // Menu toggle cleanup
        this.menuToggle?.removeEventListener(
          'click',
          this.handleMenuToggle.bind(this)
        );

        // Window events cleanup
        window.removeEventListener('resize', this.handleResize.bind(this));
        window.removeEventListener('scroll', this.handleScroll.bind(this));

        // Menu item hover cleanup
        this.menuItems?.forEach((item) => {
          item.removeEventListener(
            'mouseenter',
            this.handleMenuItemEnter.bind(this)
          );
          item.removeEventListener(
            'mouseleave',
            this.handleMenuItemLeave.bind(this)
          );
        });
      }

      private initializeHeader(): void {
        requestAnimationFrame(() => {
          this.parentElement?.classList.remove('-translate-y-full');
        });
        this.lastScrollY = window.scrollY;
      }

      private handleResize(): void {
        // Update menu height if open
        if (this.menuWrapper && !this.menuWrapper.classList.contains('h-0')) {
          this.setDynamicMenuHeight();
        }
      }

      private getCurrentScrollThreshold(): number {
        // Dynamically calculate threshold based on header height
        const headerHeight = this.headerElement?.offsetHeight || 0;
        const announcementHeight =
          window.scrollY === 0 && this.announcementBar
            ? this.announcementBar.offsetHeight
            : 0;

        return headerHeight + announcementHeight;
      }

      private handleScroll(): void {
        if (this.isScrollUpdatePending) return;

        this.isScrollUpdatePending = true;
        requestAnimationFrame(() => {
          const currentY = window.scrollY;
          const threshold = this.getCurrentScrollThreshold();
          const shouldHide =
            currentY > this.lastScrollY && currentY > threshold;

          this.parentElement?.classList.toggle('-translate-y-full', shouldHide);
          this.lastScrollY = currentY;
          this.isScrollUpdatePending = false;
        });
      }

      private setupMobileMenuAnimations(): void {
        const mobileMenuItems = this.querySelectorAll(
          '[data-animated-menu-item-mobile]'
        );
        mobileMenuItems.forEach((item, index) => {
          // Add delay based on index for staggered animation
          setTimeout(() => {
            item.classList.remove('translate-y-12', 'opacity-0');
          }, index * 50);
        });
      }

      private resetMobileMenuAnimations(): void {
        const mobileMenuItems = this.querySelectorAll(
          '[data-animated-menu-item-mobile]'
        );
        mobileMenuItems.forEach((item) => {
          item.classList.add(
            'transform',
            'transition-all',
            'duration-250',
            'ease-out',
            'translate-y-12',
            'opacity-0'
          );
        });
      }

      private handleMenuToggle(): void {
        if (!this.menuWrapper || !this.menuToggle) return;

        const isOpening = this.menuWrapper.classList.contains('opacity-0');
        this.toggleMenuVisibility(isOpening);
        this.toggleBodyScroll(isOpening);
        this.updateAriaExpanded();

        if (isOpening) {
          // Reset animations first
          this.resetMobileMenuAnimations();
          // Then trigger them with delay
          requestAnimationFrame(() => {
            this.setupMobileMenuAnimations();
          });
        } else {
          this.resetMobileMenuAnimations();
        }
      }

      private toggleMenuVisibility(isOpening: boolean): void {
        if (!this.menuWrapper) return;

        this.menuWrapper.classList.toggle('opacity-0', !isOpening);
        this.menuWrapper.classList.toggle('invisible', !isOpening);
        this.menuWrapper.classList.toggle('h-0', !isOpening);

        if (isOpening) {
          this.setDynamicMenuHeight();
        } else {
          this.menuWrapper.style.removeProperty('height');
        }
      }

      private setDynamicMenuHeight(): void {
        if (!this.menuWrapper) return;

        const headerHeight = this.headerElement?.offsetHeight || 0;
        const isAtTop = window.scrollY === 0;
        const announcementHeight =
          isAtTop && this.announcementBar
            ? this.announcementBar.offsetHeight
            : 0;

        const availableHeight =
          window.innerHeight - headerHeight - announcementHeight;
        this.menuWrapper.style.height = `${Math.max(availableHeight, 100)}px`; // Ensure minimum height
      }

      private handleMenuItemEnter(e: MouseEvent): void {
        if (!this.mainOverlay) return;

        document.body.classList.add('overflow-hidden');
        this.mainOverlay.classList.remove('hidden', 'opacity-0', 'pointer-events-none');
        this.mainOverlay.classList.add('opacity-100');

        // Un-hide the submenu from both AT and focus
        const menuItem = e.currentTarget as HTMLElement;
        const submenu = menuItem.querySelector<HTMLElement>('[data-submenu]');
        if (submenu) {
          submenu.removeAttribute('inert');
          submenu.classList.remove('invisible', 'opacity-0')
        }

        // Animate the menu list elements
        this.animateDropdownElements(menuItem);
      }

      private animateDropdownElements(menuItem: HTMLElement): void {
        // Combine all elements to animate into one array
        const elementsToAnimate = [
          ...Array.from(
            menuItem.querySelectorAll(
              '[data-animated-menu-item-list]'
            )
          ),
        ];

        // Set initial state for all elements
        elementsToAnimate.forEach((element) => {
          element.classList.add(
            'translate-y-12',
            'opacity-0',
            'transform',
            'transition-all',
            'duration-250',
            'ease-out'
          );
        });

        // Trigger reflow and animate in
        requestAnimationFrame(() => {
          elementsToAnimate.forEach((element, index) => {
            const delay = index * 50;

            setTimeout(() => {
              element.classList.remove('translate-y-12', 'opacity-0');
            }, delay);
          });
        });
      }

      private handleMenuItemLeave(e: MouseEvent): void {
        if (!this.mainOverlay) return;

        // Hide overlay and restore body scroll
        document.body.classList.remove('overflow-hidden');
        this.mainOverlay.classList.add('hidden', 'opacity-0', 'pointer-events-none');
        this.mainOverlay.classList.remove('opacity-100');

        const menuItem = e.currentTarget as HTMLElement;
        const submenu = menuItem.querySelector<HTMLElement>('[data-submenu]');
        if (submenu) {
          // If anything inside still has focus, blur it first
          const active = document.activeElement as HTMLElement | null;
          if (active && submenu.contains(active)) active.blur();

          submenu.setAttribute('inert', '');
          submenu.classList.add('invisible', 'opacity-0')
        }

        // Reset animations
        const animatedList = menuItem.querySelector<HTMLElement>(
          '[data-animated-menu-item-list]'
        );
        animatedList?.classList.add('translate-y-12', 'opacity-0');


      }

      private toggleBodyScroll(shouldLock: boolean): void {
        document.body.classList.toggle('overflow-hidden', shouldLock);
      }

      private updateAriaExpanded(): void {
        if (!this.menuToggle) return;
        const isExpanded =
          this.menuToggle.getAttribute('aria-expanded') !== 'true';
        this.menuToggle.setAttribute('aria-expanded', String(isExpanded));
      }
    }
  );
}
